import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Flex } from "../../../components/Flex";
import { Spacing } from "../../../theme/Spacing";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import isEqual from "lodash/isEqual";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Info } from "../../../components/Info";
import { UpdateUserEntriesTableSettingsDto } from "../../../model/entries-table-settings/dto/UpdateUserEntriesTableSettingsDto";
import { EntriesTableMainFields } from "../../../model/entries-table-settings/types/EntriesTableMainFields.enum";
import { entryActions } from "../../../redux/entry/entry.slice";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { AtiraToast } from "../../../utils/AtiraToast";
import { Text } from "../../../components/Text";
import { Checkbox } from "../../../utils/local-antd";
import { Button } from "../../../components/Button";
import { SettingsWrapper } from "../components/SettingsWrapper";

const StyledButton = styled(Button)`
  width: fit-content;
  margin: 0;
`;

const StyledInfo = styled(Info)`
  white-space: pre-line;
`;

const StyledList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.s};
  list-style-position: inside;
`;

const StyledCheckboxes = styled(Checkbox.Group)`
  .ant-checkbox-disabled .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.gray};
    border-color: ${({ theme }) => theme.main};
  }

  .ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner {
    background-color: ${({ theme }) => theme.lightMain} !important;
    opacity: 0.7;
  }

  .ant-checkbox-checked.ant-checkbox-disabled .ant-checkbox-inner::after {
    border-inline-end: 2px solid ${({ theme }) => theme.white};
    border-bottom: 2px solid ${({ theme }) => theme.white};
  }
`;

export const SettingsFields: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { handleSubmit, setValue, watch } = useForm<
    Pick<UpdateUserEntriesTableSettingsDto, "mainFields">
  >({
    defaultValues: {
      mainFields: tableSettings?.mainFields || [EntriesTableMainFields.EMAIL],
    },
  });
  const newMainFields = watch("mainFields");

  const onSave = async () => {
    try {
      setLoading(true);

      await dispatch(
        entryActions.updateUserTableSettings({
          mainFields: newMainFields,
          userId,
          tableId: tableSettings!._id,
        }),
      ).unwrap();
      await dispatch(entryActions.getUserTableSettings({ userId })).unwrap();

      AtiraToast.success(t("settings.update.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(entryActions.getUserTableSettings({ userId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SettingsWrapper>
      <Flex flexDirection="column" gap="l">
        <Flex flexDirection="column" gap="s">
          <Text fontWeight={"bold"}>{t("common.mainFields")}</Text>
          <Text fontSize="s" color="darkerSub">
            {t("entries.table.settings.tab-mainFields.description-2")}
          </Text>

          <StyledInfo
            info={
              <StyledList>
                {Array.from({ length: 3 }, (_, i) => i + 1).map((i) => (
                  <li key={i}>
                    {t(
                      `entries.table.settings.tab-mainFields.description.list.${i}`,
                    )}
                  </li>
                ))}
              </StyledList>
            }
            color="danger"
          />
        </Flex>
        <Flex flexDirection="column" gap="m">
          <StyledCheckboxes
            value={newMainFields}
            onChange={(checkedValues) =>
              setValue("mainFields", checkedValues as EntriesTableMainFields[])
            }
          >
            <Checkbox
              value={EntriesTableMainFields.PHONE}
              disabled={
                newMainFields?.includes(EntriesTableMainFields.PHONE) &&
                newMainFields?.length === 1
              }
            >
              {t("common.phone")}
            </Checkbox>
            <Checkbox
              value={EntriesTableMainFields.EMAIL}
              disabled={
                newMainFields?.includes(EntriesTableMainFields.EMAIL) &&
                newMainFields?.length === 1
              }
            >
              {t("common.email")}
            </Checkbox>
          </StyledCheckboxes>

          <Info info={t("entries.table.settings.tab-mainFields.description")} />
        </Flex>

        <StyledButton
          loading={loading}
          onClick={handleSubmit(onSave)}
          disabled={
            !newMainFields?.length ||
            isEqual(newMainFields, tableSettings?.mainFields)
          }
        >
          {t("common.save_changes")}
        </StyledButton>
      </Flex>
    </SettingsWrapper>
  );
};
